import { LICENSE_MODULE, selMessage } from "@selector/shared";
import { produce } from "immer";
import pluralize from "pluralize";

import { createErrorHandler } from "@/app-utils/errors.ts";
import {
  type AdminBrowser,
  type GenomeSearch,
  type InputTableQuerySpec,
  type InputUserGroupInput,
  type ListingMapEntry,
  type Organism,
  type UserGroup,
} from "@/graphql.ts";
import {
  CREATE_USERGROUP_FAILURE,
  CREATE_USERGROUP_REQUEST,
  CREATE_USERGROUP_SUCCESS,
  DELETE_USER_GROUP_FAILURE,
  DELETE_USER_GROUP_SUCCESS,
  EDIT_USER_GROUP_FAILURE,
  EDIT_USER_GROUP_SUCCESS,
  EDIT_USER_GROUPS_ASSIGNMENTS_FAILURE,
  EDIT_USER_GROUPS_ASSIGNMENTS_SUCCESS,
  FILTER_BY_TABLE_COLUMN,
  LOAD_USER_GROUPS_FAILURE,
  LOAD_USER_GROUPS_REQUEST,
  LOAD_USER_GROUPS_SUCCESS,
  LOAD_USERGROUP_COUNTS_FAILURE,
  LOAD_USERGROUP_COUNTS_REQUEST,
  LOAD_USERGROUP_COUNTS_SUCCESS,
  LOAD_USERGROUP_LISTING_FAILURE,
  LOAD_USERGROUP_LISTING_REQUEST,
  LOAD_USERGROUP_LISTING_SUCCESS,
  RESET_FILTER,
  SEARCH_GENOMES_FAILURE,
  SEARCH_GENOMES_REQUEST,
  SEARCH_GENOMES_SUCCESS,
  USER_GROUP_ADMIN_CHANGE_SELECTION,
} from "@/redux/action-types.ts";
import {
  GROUP_BY_ATTRIBUTE_CREATION_TIME,
  GROUP_BY_ATTRIBUTE_USER_GROUP,
  ORGANISM_TABLE_COLUMNS,
  SAMPLE_TYPE_TABLE_COLUMNS,
  TABLE_CONTEXT_TYPE,
  USER_TABLE_COLUMNS,
} from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { buildCreateUsergroupMessage } from "../../app-utils/alert.js";
import { notify } from "../../modules/notifications/notify.js";
import { type ITableSpecWithContext } from "../../ui-modules/content-browser/ContentBrowserTableView.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { loadUserSession } from "./auth.js";
import { isLicensed } from "./config.js";
import {
  createTableSpecForRootEntityType,
  filterByTableColumn,
  type IFilterPayload,
  type ITableColumn,
  type ITableData,
  reloadTableRows,
  resetAllFiltersAndReloadTable,
} from "./table-data.js";

/**
 * State definition
 */
export interface IUserGroupAdminState {
  content: AdminBrowser;
  isLoadingCounts: boolean;
  isLoadingListing: boolean;
  selectedEntry: ListingMapEntry;
  featureType: string;
  groupByAttribute: string;

  isLoadingUserGroups: boolean;
  userGroups: UserGroup[];
}

const initialState: IUserGroupAdminState = {
  content: null,
  isLoadingCounts: false,
  isLoadingListing: false,
  selectedEntry: null,
  featureType: null,
  groupByAttribute: null,
  isLoadingUserGroups: false,
  userGroups: [],
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_USERGROUP_COUNTS_REQUEST]: (state: IUserGroupAdminState): IUserGroupAdminState => {
    return { ...state, isLoadingCounts: true };
  },

  [LOAD_USERGROUP_LISTING_REQUEST]: (
    state: IUserGroupAdminState,
    { featureType, groupByAttribute },
  ): IUserGroupAdminState => {
    return {
      ...state,
      isLoadingListing: true,
      featureType,
      groupByAttribute,
    };
  },

  [LOAD_USERGROUP_COUNTS_SUCCESS]: (state: IUserGroupAdminState, content: AdminBrowser): IUserGroupAdminState => {
    return {
      ...state,
      isLoadingCounts: false,
      content: mergeContent(state.content, content),
    };
  },
  [LOAD_USERGROUP_LISTING_SUCCESS]: (state: IUserGroupAdminState, content: AdminBrowser): IUserGroupAdminState => {
    return {
      ...state,
      isLoadingListing: false,
      content: mergeContent(state.content, content),
    };
  },

  [LOAD_USERGROUP_COUNTS_FAILURE]: (state: IUserGroupAdminState): IUserGroupAdminState => {
    return { ...state, isLoadingCounts: false };
  },
  [LOAD_USERGROUP_LISTING_FAILURE]: (state: IUserGroupAdminState): IUserGroupAdminState => {
    return { ...state, isLoadingListing: false };
  },
  [USER_GROUP_ADMIN_CHANGE_SELECTION]: (state: IUserGroupAdminState, entry: ListingMapEntry): IUserGroupAdminState => {
    const newValue: ListingMapEntry =
      !entry || (state.selectedEntry && state.selectedEntry.key === entry.key) ? null : entry;
    return { ...state, selectedEntry: newValue };
  },
  [RESET_FILTER]: (state: IUserGroupAdminState) => {
    return { ...state, selectedEntry: null };
  },
  [FILTER_BY_TABLE_COLUMN]: (state: IUserGroupAdminState, payload) => {
    const { columnField, filterExpression } = payload;
    // if the filter has been manually changed by the user we unset the selected entry
    if (
      state.selectedEntry &&
      state.selectedEntry.columnField === columnField &&
      state.selectedEntry.tableFilter !== filterExpression
    ) {
      return { ...state, selectedEntry: null };
    }
    return state;
  },
  [LOAD_USER_GROUPS_SUCCESS]: (state: IUserGroupAdminState, userGroups: UserGroup[]): IUserGroupAdminState => {
    return {
      ...state,
      userGroups: userGroups,
      isLoadingUserGroups: false,
    };
  },
  [LOAD_USER_GROUPS_REQUEST]: (state: IUserGroupAdminState): IUserGroupAdminState => {
    return {
      ...state,
      isLoadingUserGroups: true,
    };
  },
});

/**
 * Action creators
 */
export function loadUserGroupAdminCounts(projectGroupAdminKeys: string[]) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<AdminBrowser> => {
    const userGroupAdminState: IUserGroupAdminState = getState().userGroupAdmin;

    if (!userGroupAdminState.isLoadingCounts) {
      dispatch({ type: LOAD_USERGROUP_COUNTS_REQUEST });

      return webApiClient
        .fetchUserGroupAdminCounts(projectGroupAdminKeys)
        .then((response: AdminBrowser) => {
          dispatch({
            type: LOAD_USERGROUP_COUNTS_SUCCESS,
            payload: response,
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_USERGROUP_COUNTS_FAILURE,
            alertTitle: "Fetching of Content Browser Counts",
          }),
        );
    }
  };
}

export function reloadUserGroupAdminListing() {
  return (dispatch, getState, _webApiClient: WebApiClient): Promise<AdminBrowser> => {
    const userGroupAdminState: IUserGroupAdminState = getState().userGroupAdmin;
    return dispatch(loadUserGroupAdminListing(userGroupAdminState.featureType, userGroupAdminState.groupByAttribute));
  };
}

export function loadUserGroupAdminListing(featureType: string, groupByAttribute: string) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<AdminBrowser> => {
    const userGroupAdminState: IUserGroupAdminState = getState().userGroupAdmin;

    if (!userGroupAdminState.isLoadingListing) {
      dispatch({
        type: LOAD_USERGROUP_LISTING_REQUEST,
        payload: { featureType, groupByAttribute },
      });

      return webApiClient
        .fetchAdminBrowserListing(featureType, groupByAttribute)
        .then((response: AdminBrowser) => {
          dispatch({
            type: LOAD_USERGROUP_LISTING_SUCCESS,
            payload: response,
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_USERGROUP_LISTING_FAILURE,
            alertTitle: `Fetching Admin Listing for Feature Type '${featureType}' Grouped by '${groupByAttribute}'`,
          }),
        );
    }
  };
}

export function changeGroupByAttributeFilter(entry: ListingMapEntry) {
  return (dispatch, getState) => {
    const userGroupAdminState: IUserGroupAdminState = getState().userGroupAdmin;

    const featureType: string = userGroupAdminState.featureType;
    // toggle the filter if already selected
    const filter: ListingMapEntry =
      userGroupAdminState.selectedEntry && entry && userGroupAdminState.selectedEntry.key === entry.key ? null : entry;
    const tableSpec: InputTableQuerySpec = createUserGroupAdminTableSpec(featureType);

    const filterPayload: IFilterPayload = filter
      ? createFilterPayloadForSelectedBar(entry, tableSpec)
      : ({ tableSpec } as IFilterPayload);
    dispatch(filterByTableColumn(filterPayload, true));
    dispatch({
      type: USER_GROUP_ADMIN_CHANGE_SELECTION,
      payload: filter,
    });
  };
}

export function createUserGroupAdminTableSpec(pluralFeatureType: string) {
  const ret: ITableSpecWithContext = createTableSpecForRootEntityType(pluralize.singular(pluralFeatureType));
  ret.context = TABLE_CONTEXT_TYPE.ADMIN;
  return ret;
}

// which organism table columns to be shown IN ADDITION AFTER THE FIXED COLUMNS when navigating through content browser "group by" attributes
export const VISIBLE_ORGANISM_TABLE_FIELDS_BY_ATTRIBUTE = {
  [GROUP_BY_ATTRIBUTE_USER_GROUP]: [
    ORGANISM_TABLE_COLUMNS.SPECIES,
    ORGANISM_TABLE_COLUMNS.USER_GROUPS,
    ORGANISM_TABLE_COLUMNS.REF_GENOME,
  ],
  [GROUP_BY_ATTRIBUTE_CREATION_TIME]: [ORGANISM_TABLE_COLUMNS.SPECIES, ORGANISM_TABLE_COLUMNS.CREATION_DATE],
};

// which user table columns to be shown IN ADDITION AFTER THE FIXED COLUMNS when navigating through content browser "group by" attributes
export const VISIBLE_USER_TABLE_FIELDS_BY_ATTRIBUTE = {
  [GROUP_BY_ATTRIBUTE_USER_GROUP]: [USER_TABLE_COLUMNS.USER_GROUPS],
};

// which user table columns to be shown IN ADDITION AFTER THE FIXED COLUMNS when navigating through content browser "group by" attributes
export const VISIBLE_SAMPLE_TYPE_TABLE_FIELDS_BY_ATTRIBUTE = {
  [GROUP_BY_ATTRIBUTE_USER_GROUP]: [SAMPLE_TYPE_TABLE_COLUMNS.PROJECT_GROUPS],
};

function createFilterPayloadForSelectedBar(
  selectedEntry: ListingMapEntry,
  tableSpec: InputTableQuerySpec,
): IFilterPayload {
  return {
    columnField: selectedEntry.columnField,
    tableSpec,
    filterExpression: selectedEntry.tableFilter,
  };
}

export function searchGenomes(searchTerm: string) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Organism[]> => {
    dispatch({ type: SEARCH_GENOMES_REQUEST, payload: searchTerm });

    return webApiClient
      .searchGenomes(searchTerm)
      .then((response: GenomeSearch) => {
        dispatch({
          type: SEARCH_GENOMES_SUCCESS,
          payload: searchTerm,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SEARCH_GENOMES_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function validateUsergroupNameIsNotUsed(usergroupName: string, _usergroupId?: number) {
  return (_dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    if (!usergroupName) {
      // can happen because the async validation does take the previous value, and therefore this actual value might be empty
      return new Promise<boolean>((resolve) => {
        resolve(false);
      });
    }
    return webApiClient.isUsergroupNameDefined(usergroupName, null);
  };
}

/// Create Project
function initiateCreateUsergroupRequest(payload: any) {
  return {
    type: CREATE_USERGROUP_REQUEST,
    payload: payload,
  };
}

function createUsergroupRequestSuccess(response: UserGroup) {
  return {
    type: CREATE_USERGROUP_SUCCESS,
    payload: response,
  };
}

export function createNewUsergroup(payload: InputUserGroupInput): any {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<UserGroup> => {
    dispatch(initiateCreateUsergroupRequest(payload));
    return webApiClient
      .saveUsergroup(payload)
      .then((usergroup: UserGroup) => {
        const trackerLicensed = isLicensed(getState().config.configuration, LICENSE_MODULE.TRACKER);
        dispatch(createUsergroupRequestSuccess(usergroup));
        notify.info(
          {
            title: selMessage({ id: "admin.userGroup.created.successTitle" }),
            children: buildCreateUsergroupMessage(usergroup, trackerLicensed),
          },
          { autoClose: 10000 },
        );

        dispatch(reloadUserGroupAdminListing());
        dispatch(loadUserGroups());
        dispatch(loadUserSession()); // because contains the user groups of the current user, might have changed
        dispatch(reloadTableRows(getState().tableData.activeTable));
        return usergroup;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_USERGROUP_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadUserGroups() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_USER_GROUPS_REQUEST });

    return webApiClient
      .fetchUserGroups()
      .then((userGroups: UserGroup[]) => {
        dispatch({
          type: LOAD_USER_GROUPS_SUCCESS,
          payload: userGroups,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_USER_GROUPS_FAILURE,
          alertTitle: "Fetching of User Groups Failed",
        }),
      );
  };
}

export function editMultipleUserGroupAssignments(assignments: InputUserGroupInput[]) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return Promise.all(assignments.map((assignment: InputUserGroupInput) => webApiClient.modifyUserGroup(assignment)))
      .then((_values) => {
        dispatch({
          type: EDIT_USER_GROUPS_ASSIGNMENTS_SUCCESS,
        });
        dispatch(reloadUserGroupAdminListing());
        dispatch(loadUserGroups());
        dispatch(loadUserSession()); // because contains the user groups of the current user, might have changed
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: EDIT_USER_GROUPS_ASSIGNMENTS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function editSingleUserGroup(userGroup: InputUserGroupInput) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    return webApiClient
      .modifyUserGroup(userGroup)
      .then((_values) => {
        dispatch({
          type: EDIT_USER_GROUP_SUCCESS,
        });
        dispatch(reloadUserGroupAdminListing());
        dispatch(loadUserGroups());
        dispatch(loadUserSession()); // because contains the user groups of the current user, might have changed

        const selectedEntry: ListingMapEntry = getState().userGroupAdmin.selectedEntry;
        // if the currently selected entry is the one we edit, we reset the selection in order to prevent any data caching problem
        if (selectedEntry && userGroup.userGroupId === Number.parseInt(selectedEntry.key, 10)) {
          dispatch(resetAllFiltersAndReloadTable(getState().tableData.activeTable));
        } else {
          // ... otherwise we simply reload the current rows
          dispatch(reloadTableRows(getState().tableData.activeTable));
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: EDIT_USER_GROUP_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function deleteSingleUserGroup(userGroup: UserGroup) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient
      .deleteUserGroup(userGroup.id)
      .then((_values) => {
        dispatch({ type: DELETE_USER_GROUP_SUCCESS });
        notify.info(
          {
            title: selMessage({ id: "admin.userGroup.deleted.successTitle" }),
            children: selMessage({
              id: "admin.userGroup.deleted.successMessage",
              values: { userGroupName: userGroup.name },
            }),
          },
          { autoClose: 10000 },
        );
        dispatch(reloadUserGroupAdminListing());
        dispatch(loadUserGroups());
        dispatch(loadUserSession()); // because contains the user groups of the current user, might have changed

        const selectedEntry: ListingMapEntry = getState().userGroupAdmin.selectedEntry;
        // if the currently selected entry is the one we edit, we reset the selection in order to prevent any data caching problem
        if (selectedEntry && userGroup.id === Number.parseInt(selectedEntry.key, 10)) {
          dispatch(resetAllFiltersAndReloadTable(getState().tableData.activeTable));
        } else {
          // ... otherwise we simply reload the current rows
          dispatch(reloadTableRows(getState().tableData.activeTable));
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_USER_GROUP_FAILURE,
          alertTitle: "User Group Deletion Failed",
        }),
      );
  };
}

/** Utils **/
// allows to merge all props of e.g. content.organisms so that we keep "count" and other listing intact ;
// the difficulty is that sub-prop values are coming from multiple requests and we should correctly merge
// by taking into account the props that have been deleted for real !
// a standard recursive merge (e.g. from lodash) does not work because sometimes we add and sometimes we remove props
// and these should be reflected in the redux module, therefore we need to merge at a specific level of hierarchy.
export function mergeContent(content: AdminBrowser, updatedContent: AdminBrowser): AdminBrowser {
  if (!content) {
    return updatedContent;
  }

  let newContent: AdminBrowser = content;
  Object.keys(updatedContent)
    .filter((key: string) => key !== "__typename")
    .forEach((key: string) => {
      if (content[key]) {
        newContent = produce(newContent, (draft) => {
          draft[key] = { ...draft[key], ...updatedContent[key] };
        });
      } else {
        newContent = produce(newContent, (draft) => {
          draft[key] = updatedContent[key];
        });
      }
    });
  return newContent;
}

export function findUserGroupColumnFieldFromTableData(tableData: ITableData): string {
  const userGroupColumn: ITableColumn = tableData.columns.find((column: ITableColumn) =>
    column.field.includes(ORGANISM_TABLE_COLUMNS.USER_GROUPS),
  );
  return userGroupColumn && userGroupColumn.field;
}
